import { DatePickerInput } from '@creditornot/cb-date-picker';
import { DateTime } from 'luxon';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import TimePicker from 'common/components/weekly-schedules/TimePairInput/TimePicker';

import { MenuLocaleContext } from '../MenuLocaleContext';

type Props = {
  onChange: (value: number | null) => void;
  value: number | null;
};

const SCHEDULING_LIMIT_DAYS = 30;

const Root = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
`;

const StyledTimePicker = styled(TimePicker)`
  width: 64px;
  margin-inline-start: 8px;
`;

const ScheduleDatePicker = ({ onChange, value }: Props) => {
  const { timezone } = useContext(MenuLocaleContext);
  const intl = useIntl();

  const initialValues = useMemo(() => {
    const valueAsDateTime = value ? DateTime.fromMillis(value).setZone(timezone) : null;

    return {
      dateValue: valueAsDateTime ? valueAsDateTime.toJSDate() : undefined,
      timeValue: valueAsDateTime ? valueAsDateTime.hour * 60 + valueAsDateTime.minute : 0,
      inputValue: valueAsDateTime ? valueAsDateTime.toLocaleString(DateTime.DATETIME_SHORT) : '',
    };
  }, [timezone, value]);

  useEffect(() => {
    setDateValue(initialValues.dateValue);
    setTimeValue(initialValues.timeValue);
  }, [initialValues]);

  const [dateValue, setDateValue] = useState<Date | undefined>(initialValues.dateValue);
  const [timeValue, setTimeValue] = useState<number>(initialValues.timeValue);

  useEffect(() => {
    // Should always be defined since we disable the "Done" button if undefined
    // But check to satisfy TS
    if (dateValue) {
      const date = DateTime.fromJSDate(dateValue, {
        zone: timezone,
      }).startOf('day');

      const combinedDate = date.plus({ minutes: timeValue });

      onChange(combinedDate.toMillis());
    }
  }, [dateValue, timeValue, onChange, timezone]);

  return (
    <Root>
      <DatePickerInput
        placeholder={intl.formatMessage({ id: 'date-time.date-picker-placeholder' })}
        disabledDays={{
          before: DateTime.now().toJSDate(),
          after: DateTime.now().plus({ days: SCHEDULING_LIMIT_DAYS }).toJSDate(),
        }}
        firstDayOfWeek={1}
        value={dateValue}
        onChange={val => setDateValue(val)}
        closeOnChange
        popoverZIndex={100002}
      />

      <StyledTimePicker value={timeValue} onChange={val => setTimeValue(val)} />
    </Root>
  );
};

export default ScheduleDatePicker;
